import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, BookOpen, Clock, TrendingUp } from "lucide-react";
import Seo, { breadcrumbJsonLd } from "../components/Seo";

/** Curated investment/education guides. Keep content static + long-form so SEO indexes the pages.
 *  Each guide is authored in Markdown-ish JSX. Not admin-editable yet (Phase 3 backlog). */
export const GUIDES = [
    {
        slug: "why-invest-in-nagpur-real-estate-2026",
        title: "Why invest in Nagpur real estate in 2026",
        excerpt: "Nagpur's Wardha Road, MIHAN and Samruddhi Mahamarg corridors are compounding at 12-18% CAGR. Here's the data-backed case for buying now.",
        readMin: 6,
        category: "Investment",
        hero: "https://images.unsplash.com/photo-1560518883-ce09059eeffa?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
        content: (
            <>
                <p>Nagpur is quietly becoming India’s most under-rated Tier-1 real-estate story. Between MIHAN’s aerospace hub, the Samruddhi Mahamarg expressway (Mumbai-Nagpur), Metro Phase-2 and a growing IT/BPO belt around Wardha Road, land values have compounded 12-18% CAGR over the last five years — outpacing Pune, Hyderabad and even parts of Bengaluru.</p>
                <h3>1. Infrastructure catalysts</h3>
                <p>MIHAN’s 4,354-acre SEZ has attracted Boeing, Air India MRO, TCS, HCL and Infosys. The 701-km Samruddhi Mahamarg (fully operational since 2024) has cut Mumbai commute to under 7 hours, unlocking Nagpur as a weekend-home hub. Metro Phase-2 adds 43 km of connectivity by 2027.</p>
                <h3>2. Where to buy</h3>
                <p><strong>Wardha Road</strong> (₹5,000-9,000/sqft residential) — mature, high liquidity. <strong>MIHAN &amp; Jamtha</strong> (₹3,500-6,500/sqft) — commercial-adjacent, best 5-year growth. <strong>Beltarodi &amp; Mohgaon</strong> (₹2,000-4,000/sqft) — early-stage plots, 2-3× upside potential. <strong>Civil Lines</strong> (₹8,500-14,000/sqft) — premium heritage lifestyle, ultra-thin supply.</p>
                <h3>3. Investment vehicles</h3>
                <p>For ₹15-30L: freehold plots in Beltarodi / Mohgaon. For ₹50L-1Cr: 2/3 BHK apartments on Wardha Road. For ₹1-3Cr: commercial units in MIHAN SEZ or Civil Lines duplexes. Bank auction properties (see our Auctions section) frequently deliver 20-35% discount to market.</p>
                <h3>4. Rental yield</h3>
                <p>Nagpur delivers 3.5-5.5% gross rental yield vs. 2-3% in Mumbai. Wardha Road 2BHK rents ₹15-22K/month against ₹35-45L acquisition cost.</p>
                <h3>5. Watch-outs</h3>
                <p>Always verify RERA registration, encumbrance certificate (30-year chain) and NA (non-agricultural) conversion for plots. Our verified <Link className="text-gold-700 underline" to="/advocates">Property Advocates</Link> handle end-to-end due diligence for a fixed fee.</p>
            </>
        ),
    },
    {
        slug: "buying-your-first-home-checklist-india",
        title: "First-time home buyer checklist for India",
        excerpt: "The 21-point pre-purchase, financing and registration checklist every first-time buyer should tick off before signing the dotted line.",
        readMin: 8,
        category: "Buyer Guide",
        hero: "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
        content: (
            <>
                <p>Buying your first home is India’s biggest financial decision. Miss a checkpoint and it can cost ₹10-30 lakh in surprises or years of legal drama. Here’s our field-tested 21-point checklist.</p>
                <h3>A. Discovery (Days 1-14)</h3>
                <p><strong>1. Set a realistic budget.</strong> EMI should stay under 40% of monthly take-home. <strong>2. Pick 3 shortlisted localities</strong> based on office commute, schools and 5-year appreciation potential. <strong>3. Filter by RERA-verified projects only.</strong> Use the “Verified“ toggle in our <Link className="text-gold-700 underline" to="/properties">Property search</Link>. <strong>4. Physically visit at least 5 sites.</strong> Never buy blind.</p>
                <h3>B. Due diligence (Days 15-30)</h3>
                <p><strong>5. Title chain verification</strong> — 30-year history through a property advocate. <strong>6. Encumbrance certificate</strong> from the sub-registrar. <strong>7. Approved building plan &amp; occupancy certificate.</strong> <strong>8. NOC from society/builder.</strong> <strong>9. Property tax paid-up receipts.</strong> <strong>10. RERA registration number cross-check</strong> on the state RERA portal.</p>
                <h3>C. Financing (Days 20-40)</h3>
                <p><strong>11. Compare 3 home loan quotes</strong> — see our <Link className="text-gold-700 underline" to="/loan-services">Loan Services</Link>. <strong>12. Get pre-approval</strong> to negotiate faster. <strong>13. Factor 12-15% overhead</strong> — stamp duty (6-7%), registration (1%), GST on under-construction (5%), loan processing (0.5-1%), interiors (₹1,200-3,000/sqft).</p>
                <h3>D. Negotiation &amp; agreement (Days 40-55)</h3>
                <p><strong>14. Negotiate 5-12% off listed price.</strong> Ask for waived club/car-park charges. <strong>15. Read the sale agreement</strong> line-by-line with your advocate. <strong>16. Confirm possession date + penalty clauses</strong> (₹5-15/sqft/month for delay is standard).</p>
                <h3>E. Registration &amp; possession (Days 55-70)</h3>
                <p><strong>17. E-stamp duty payment</strong> before deed execution. <strong>18. Registration at sub-registrar’s office</strong> (both parties + 2 witnesses + Aadhaar/PAN). <strong>19. Mutation in municipal records.</strong> <strong>20. Property tax name change.</strong> <strong>21. Home insurance</strong> — 0.2-0.5% annual premium covers structure and contents.</p>
                <p>Stuck? Email our advisor at <strong>globalpropertymart95@gmail.com</strong> — free 30-min email consultation for first-time buyers.</p>
            </>
        ),
    },
    {
        slug: "bank-auction-property-buying-guide",
        title: "How to buy bank auction properties safely",
        excerpt: "Bank/SARFAESI auctions offer 20-35% discount vs. market but hide legal landmines. Our step-by-step guide to bidding, EMD and possession.",
        readMin: 7,
        category: "Advanced",
        hero: "https://images.unsplash.com/photo-1554224155-1696413565d3?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
        content: (
            <>
                <p>Bank auctions under the SARFAESI Act, DRT and Court orders are one of the least-known but highest-return segments in Indian real estate — regularly delivering 20-35% discount to market value. But navigate them wrong and you inherit tenants, litigation or unresolved dues.</p>
                <h3>Types of auctions</h3>
                <p><strong>SARFAESI</strong> — Bank-driven; symbolic or physical possession. <strong>DRT/DRAT</strong> — Debt Recovery Tribunal; court-monitored, higher legal certainty. <strong>Court-ordered</strong> — Under IBC/liquidation, often the safest but slowest.</p>
                <h3>Before you bid</h3>
                <p>1. Inspect the property physically (banks publish inspection windows in the e-auction notice). 2. Confirm possession type — <em>physical possession</em> means the bank hands you keys; <em>symbolic</em> means the previous owner/tenant may still occupy. 3. Get a title report from an advocate (30-year chain, encumbrance). 4. Check outstanding society dues, property tax and electricity bills — buyer inherits these.</p>
                <h3>EMD and bidding</h3>
                <p>Earnest Money Deposit (EMD) is usually 10% of reserve price, submitted via DD or e-payment before the auction window. Bidding is on the bank’s e-auction portal (MSTC, NeML, e-Procurement). The winner deposits an additional 15% within 24 hours and the remaining 75% within 15 days.</p>
                <h3>What to watch for</h3>
                <p><strong>Physical possession first</strong> — never bid on symbolic-possession listings without a lawyer. <strong>Encumbrance certificate</strong> — many auction sale certificates carry a “as-is-where-is, without warranty“ clause; only clean-title lots are safe. <strong>Occupation status</strong> — if tenants exist, eviction can take 6-24 months.</p>
                <h3>Financing</h3>
                <p>Most nationalised banks offer 60-70% financing on auction properties, but only for physical-possession lots with clean title. Get pre-approval — auction closures require full payment inside 15 days.</p>
                <p>Browse verified live and upcoming bank auctions in our <Link className="text-gold-700 underline" to="/auctions">Auctions section</Link>.</p>
            </>
        ),
    },
    {
        slug: "home-loan-eligibility-tips-2026",
        title: "How to maximise your home loan eligibility",
        excerpt: "6 lender-tested tricks to increase your sanctioned home loan by 30-50% without changing your salary.",
        readMin: 5,
        category: "Finance",
        hero: "https://images.unsplash.com/photo-1554224154-26032ffc0d07?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
        content: (
            <>
                <p>Your home loan eligibility isn’t fixed by your salary alone. Six proven levers can bump the sanctioned amount by 30-50%.</p>
                <h3>1. Add co-applicants strategically</h3>
                <p>Adding a working spouse can nearly double the sanction (banks club both incomes at ~50%-60% FOIR). Both co-applicants get income tax benefits under Section 24(b) and 80C.</p>
                <h3>2. Extend the tenure</h3>
                <p>Increasing tenure from 15 to 25 years can boost eligibility 40%. But calculate the total interest paid — a ₹50L loan @ 8.5% costs ₹42L extra interest over 25 years vs. 15 years.</p>
                <h3>3. Consolidate credit cards</h3>
                <p>Every ₹10K/month in credit card outgo reduces eligible EMI by ₹10K. Pay off/close low-limit cards 3 months before applying — improves CIBIL by 30-50 points.</p>
                <h3>4. Include variable pay</h3>
                <p>Bonuses, commissions, overtime and rental income (via Form 26AS) can all be added. Bank needs 2-3 year track record.</p>
                <h3>5. Choose a higher LTV lender</h3>
                <p>HDFC, ICICI and SBI offer 75-80% LTV. LIC HFC, Bajaj Housing offer up to 90% for select profiles (though at 25-50 bps higher rate).</p>
                <h3>6. Get pre-approval before house-hunting</h3>
                <p>Pre-approved buyers close 40% faster and can negotiate 3-5% off the asking price.</p>
                <p>Compare 20+ lenders on our <Link className="text-gold-700 underline" to="/loan-services">Loan Services</Link> page with a built-in EMI calculator.</p>
            </>
        ),
    },
    {
        slug: "rera-checks-before-buying-under-construction",
        title: "RERA checks every under-construction buyer must do",
        excerpt: "The 8-point RERA verification framework that protects you from delayed possession, undisclosed liens and construction shortcuts.",
        readMin: 5,
        category: "Buyer Guide",
        hero: "https://images.unsplash.com/photo-1517649763962-0c623066013b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
        content: (
            <>
                <p>The Real Estate Regulation Act (RERA), 2016 is your biggest legal shield when buying under-construction property. Here’s how to use it right.</p>
                <h3>1. Cross-check the RERA registration number</h3>
                <p>Every project sold under RERA must have a unique registration ID (e.g. MahaRERA P51600123456). Verify it on <em>maharera.mahaonline.gov.in</em> before paying any token. If it’s not registered — walk away.</p>
                <h3>2. Confirm the project stage</h3>
                <p>The RERA portal shows quarterly progress reports. Delays past the RERA-registered possession date automatically trigger interest at MCLR + 2% payable to buyers.</p>
                <h3>3. Read the sample sale agreement</h3>
                <p>RERA mandates a model sale agreement — check for the possession date clause, delay penalty formula, defect liability period (5 years) and carpet area (not built-up).</p>
                <h3>4. Verify the escrow account</h3>
                <p>RERA requires 70% of buyer payments to be parked in a project-specific escrow account, usable only for that project’s construction and land cost.</p>
                <h3>5. Check the Approved Plans upload</h3>
                <p>RERA portal has copies of approved building plans, tower NOCs, environmental clearance, layout approvals. If missing — red flag.</p>
                <h3>6. Look up complaints history</h3>
                <p>Search the builder’s promoter name for past RERA complaints, penalties or de-registrations.</p>
                <h3>7. Ensure carpet-area disclosure</h3>
                <p>RERA outlaws “super built-up“ area sales. Price must be quoted on carpet area (which is 20-30% smaller). This alone can save you ₹10-25L on a ₹1Cr flat.</p>
                <h3>8. Retain the RERA number in the sale deed</h3>
                <p>The registration number and possession date must be quoted verbatim in the registered sale agreement.</p>
            </>
        ),
    },
    {
        slug: "vastu-tips-buying-plot-nagpur",
        title: "Vastu-friendly plot buying tips for Nagpur",
        excerpt: "5 Vastu-compliant plot selection rules — direction, road, cuts, water & slope — that also correlate with re-sale value.",
        readMin: 4,
        category: "Culture",
        hero: "https://images.unsplash.com/photo-1495521821757-a1efb6729352?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
        content: (
            <>
                <p>Whether or not you follow Vastu Shastra, the principles it codifies (sunlight, ventilation, drainage) genuinely correlate with plot quality and re-sale demand. Here are five buying rules that satisfy both.</p>
                <h3>1. Direction — North &amp; East beat South &amp; West</h3>
                <p>North-facing and East-facing plots command a 5-15% premium in Nagpur because they get more morning sun and less afternoon heat. Wardha Road plots facing east typically re-sell 40% faster.</p>
                <h3>2. Road placement</h3>
                <p>Plots with roads on North &amp; East (or all four sides) are ideal. Plots with roads only on South &amp; West heat up faster and are 8-12% cheaper — but re-sell slower.</p>
                <h3>3. Shape — regular over irregular</h3>
                <p>Square or rectangular plots (1:1 to 1:1.5 ratio) are easiest to build on and re-sell. Triangular, L-shaped or cut plots have lower demand and 10-20% lower per-sqft prices.</p>
                <h3>4. Corner cuts</h3>
                <p>North-East corner cut (called <em>Ishaan</em>) is considered inauspicious and reduces re-sale demand. South-West corner cut has minimal impact.</p>
                <h3>5. Slope &amp; water</h3>
                <p>A gentle slope from South-West (higher) to North-East (lower) allows natural drainage. Also check the borewell yield — 4-6 inch main pipe with 1,500+ litres/hour is the Nagpur norm.</p>
                <p>Browse RERA-verified plots across <Link className="text-gold-700 underline" to="/property-in-nagpur">30+ Nagpur localities</Link>.</p>
            </>
        ),
    },
];

export default function InvestmentGuides() {
    return (
        <div className="min-h-screen bg-white">
            <Seo
                title="Real Estate Investment Guides — Nagpur, RERA, Home Loans, Bank Auctions"
                description="Free, in-depth guides on real estate investing in Nagpur, RERA due diligence, first-time home buying, bank auction bidding, home loan optimisation and more."
                path="/guides"
                keywords="real estate investment guides India, RERA guide, first-time home buyer checklist, bank auction property, home loan eligibility"
                jsonLd={{
                    "@context": "https://schema.org",
                    "@type": "Blog",
                    name: "Global Property Mart — Investment Guides",
                    description: "Data-backed real-estate guides for Indian buyers, sellers and investors.",
                    blogPost: GUIDES.map((g) => ({
                        "@type": "BlogPosting",
                        headline: g.title,
                        description: g.excerpt,
                        url: `/guides/${g.slug}`,
                    })),
                }}
            />

            <section className="text-on-image relative bg-gradient-to-br from-[#001a3d] via-[#002657] to-[#12234b] border-b border-gold-500/10">
                <div className="absolute inset-0 grid-pattern opacity-25" />
                <div className="relative max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 py-16 sm:py-20">
                    <div className="label-overline mb-3 flex items-center gap-2">
                        <TrendingUp className="w-3.5 h-3.5" /> Investment Guides
                    </div>
                    <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white max-w-3xl leading-tight">
                        Real-estate insights that <span className="text-gold-500">actually help.</span>
                    </h1>
                    <p className="text-white/70 text-base sm:text-lg mt-5 max-w-2xl">
                        Data-backed guides written by property advocates, RBI-approved loan agents and long-time Nagpur developers — updated for 2026 rules.
                    </p>
                </div>
            </section>

            <section className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 py-14 sm:py-20">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="guides-grid">
                    {GUIDES.map((g) => (
                        <Link
                            key={g.slug}
                            to={`/guides/${g.slug}`}
                            data-testid={`guide-card-${g.slug}`}
                            className="group block rounded-2xl bg-white border border-navy-500/10 overflow-hidden hover:border-gold-500/50 hover-lift transition-colors"
                        >
                            <div className="aspect-[16/10] overflow-hidden">
                                <img src={g.hero} alt={g.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                            </div>
                            <div className="p-5 space-y-3">
                                <div className="flex items-center gap-3 text-[11px] text-navy-500/60">
                                    <span className="px-2 py-0.5 rounded-full bg-gold-500/15 text-gold-700 font-bold uppercase tracking-wider">
                                        {g.category}
                                    </span>
                                    <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {g.readMin} min read</span>
                                </div>
                                <h3 className="font-heading font-bold text-navy-500 text-lg leading-snug group-hover:text-gold-700 transition-colors line-clamp-2">
                                    {g.title}
                                </h3>
                                <p className="text-sm text-navy-500/70 leading-relaxed line-clamp-3">{g.excerpt}</p>
                                <div className="inline-flex items-center gap-1.5 text-xs font-bold text-gold-600 group-hover:text-gold-800 transition-colors pt-1">
                                    Read guide <ArrowRight className="w-3.5 h-3.5" />
                                </div>
                            </div>
                        </Link>
                    ))}
                </div>
            </section>
        </div>
    );
}

/** Individual guide page. */
export function GuideDetail() {
    const { slug } = useParams();
    const guide = GUIDES.find((g) => g.slug === slug);

    if (!guide) {
        return (
            <div className="min-h-screen bg-white flex flex-col items-center justify-center text-center px-6 py-20">
                <BookOpen className="w-12 h-12 text-gold-500/60 mb-4" />
                <h1 className="font-heading text-3xl font-bold text-navy-500 mb-3">Guide not found</h1>
                <Link to="/guides" className="text-on-image inline-flex items-center gap-2 px-6 py-2.5 rounded-md bg-gold-500 text-white font-semibold hover:bg-gold-600">
                    <ArrowLeft className="w-4 h-4" /> Back to guides
                </Link>
            </div>
        );
    }

    const related = GUIDES.filter((g) => g.slug !== guide.slug).slice(0, 3);

    return (
        <div className="min-h-screen bg-white">
            <Seo
                title={guide.title}
                description={guide.excerpt}
                path={`/guides/${guide.slug}`}
                keywords={`${guide.category}, real estate ${guide.category.toLowerCase()}, Nagpur real estate, RERA, home buying`}
                image={guide.hero}
                jsonLd={[
                    breadcrumbJsonLd([
                        { name: "Home", url: "/" },
                        { name: "Investment Guides", url: "/guides" },
                        { name: guide.title, url: `/guides/${guide.slug}` },
                    ]),
                    {
                        "@context": "https://schema.org",
                        "@type": "BlogPosting",
                        headline: guide.title,
                        description: guide.excerpt,
                        image: guide.hero,
                        author: { "@type": "Organization", name: "Global Property Mart" },
                        publisher: { "@type": "Organization", name: "Global Property Mart" },
                        articleSection: guide.category,
                    },
                ]}
            />

            {/* HERO */}
            <div className="relative">
                <div className="aspect-[21/9] sm:aspect-[21/8] w-full overflow-hidden">
                    <img src={guide.hero} alt={guide.title} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-navy-950/95 via-navy-950/60 to-navy-950/30" />
                </div>
                <div className="text-on-image absolute inset-x-0 bottom-0 p-5 sm:p-10 lg:p-16">
                    <div className="max-w-4xl mx-auto">
                        <Link to="/guides" className="text-xs text-gold-500 inline-flex items-center gap-1.5 mb-3 hover:text-gold-100">
                            <ArrowLeft className="w-3.5 h-3.5" /> All guides
                        </Link>
                        <div className="flex items-center gap-3 mb-3">
                            <span className="px-2.5 py-0.5 rounded-full bg-gold-500 text-navy-900 text-[11px] font-bold uppercase tracking-wider">
                                {guide.category}
                            </span>
                            <span className="text-xs text-white/70 inline-flex items-center gap-1">
                                <Clock className="w-3 h-3" /> {guide.readMin} min read
                            </span>
                        </div>
                        <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight max-w-3xl">
                            {guide.title}
                        </h1>
                    </div>
                </div>
            </div>

            {/* CONTENT */}
            <article className="max-w-3xl mx-auto px-5 sm:px-6 py-14 sm:py-20">
                <p className="text-lg sm:text-xl text-navy-500/75 leading-relaxed mb-8 font-light border-l-4 border-gold-500 pl-5 italic">
                    {guide.excerpt}
                </p>
                <div className="prose-guide space-y-5 text-navy-500 leading-relaxed text-base">
                    {guide.content}
                </div>
            </article>

            {/* RELATED */}
            {related.length > 0 && (
                <section className="bg-[#FAFAF7] py-14 sm:py-20 border-t border-navy-500/10">
                    <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
                        <h2 className="font-heading text-2xl sm:text-3xl font-bold text-navy-500 mb-8">Related guides</h2>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            {related.map((r) => (
                                <Link
                                    key={r.slug}
                                    to={`/guides/${r.slug}`}
                                    className="group block rounded-2xl bg-white border border-navy-500/10 overflow-hidden hover:border-gold-500/50 hover-lift"
                                >
                                    <div className="aspect-[16/10] overflow-hidden">
                                        <img src={r.hero} alt={r.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                                    </div>
                                    <div className="p-5">
                                        <div className="text-[11px] text-gold-700 font-bold uppercase tracking-wider mb-2">{r.category}</div>
                                        <h3 className="font-heading font-bold text-navy-500 text-base leading-snug line-clamp-2 group-hover:text-gold-700">
                                            {r.title}
                                        </h3>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </div>
                </section>
            )}
        </div>
    );
}
